/**
 * Normalización/resolución del tipo de expediente.
 *
 * Vive en shared porque lo necesitan el backend (al crear el expediente) y el
 * frontend (validación previa de la columna `tipo_expediente` del Excel).
 */
const {
  ALL_EXPEDIENT_TYPES,
  DEFAULT_EXPEDIENT_TYPE,
  EXPEDIENT_TYPE_ALIASES,
  EXPEDIENT_TYPES,
} = require('../constants/expedient');
const { COMPANY_SERVICE_TYPES } = require('../constants/company');

/**
 * Normaliza un valor de tipo de expediente a su forma canónica del enum, o
 * devuelve null si no es un tipo válido. Tolera espacios, mayúsculas, guiones
 * bajos y alias/etiquetas ES (p.ej. "Account_Receivable", "Accounts Receivable",
 * "Extrajudicial" → forma canónica).
 *
 * @param {unknown} value
 * @returns {string|null}
 */
function normalizeExpedientType(value) {
  if (value == null) return null;
  const normalized = String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-');
  if (ALL_EXPEDIENT_TYPES.includes(normalized)) return normalized;
  // hasOwnProperty: evita devolver miembros heredados de Object.prototype
  // (p.ej. "constructor", "__proto__") ante input malicioso del importador.
  return Object.prototype.hasOwnProperty.call(EXPEDIENT_TYPE_ALIASES, normalized)
    ? EXPEDIENT_TYPE_ALIASES[normalized]
    : null;
}

/**
 * ¿Puede un expediente de esta empresa estar en este `type`?
 *
 * Todo lo de AR (facturación por euros gestionados, «Pasar a recobro», la
 * revisión de la IA por el cliente) solo existe si la empresa dueña tiene AR
 * contratado. Un expediente AR de una empresa sin AR queda en tierra de nadie:
 * no se factura y nadie de la empresa puede escalarlo (decisión del 14/10/2026).
 * Los demás tipos no dependen del servicio de la empresa.
 *
 * Es la ÚNICA definición de la regla: la usan la importadora (al resolver el
 * tipo) y la ficha del admin (al cambiarlo).
 *
 * @param {unknown} type                 tipo del expediente (se normaliza)
 * @param {unknown} companyServiceType   `service_type` de la empresa dueña
 * @returns {boolean}
 */
function isTypeAllowedForCompany(type, companyServiceType) {
  if (normalizeExpedientType(type) !== EXPEDIENT_TYPES.ACCOUNT_RECEIVABLE) return true;
  return companyServiceType === COMPANY_SERVICE_TYPES.ACCOUNT_RECEIVABLE;
}

/** Por qué una empresa no puede tener expedientes AR. */
const AR_REQUIRES_AR_COMPANY_MESSAGE =
  'La empresa no tiene gestión propia contratada: sus expedientes no pueden estar en gestión propia';

/**
 * Resuelve el `type` de un expediente según la cadena de prioridad de negocio:
 *   1. Columna del importador (override por fila) — si es válida y la empresa
 *      puede tener ese tipo (isTypeAllowedForCompany), manda.
 *   2. company.service_type (default del tenant) — si es válido.
 *   3. DEFAULT_EXPEDIENT_TYPE (fallback del sistema: extra-judicial).
 *
 * Si la columna pide AR y la empresa no lo tiene, la fila NO se rechaza: cae al
 * paso 2 y devuelve un aviso en `warning` para que lo vea quien importa.
 *
 * Es una función pura: no toca la BD. El caller resuelve company.service_type.
 *
 * OJO: un valor de columna NO reconocido cae al default. La importadora ya no
 * llega hasta aquí con basura — la rechaza antes el validador, para no crear el
 * expediente con un tipo distinto del que puso el cliente.
 *
 * @param {{ importerType?: unknown, companyServiceType?: unknown }} params
 * @returns {{ type: string, warning: string|null }} `type` del enum ALL_EXPEDIENT_TYPES
 */
function resolveExpedientTypeWithWarning({ importerType, companyServiceType } = {}) {
  const fallback = normalizeExpedientType(companyServiceType) || DEFAULT_EXPEDIENT_TYPE;
  const requested = normalizeExpedientType(importerType);
  if (!requested) return { type: fallback, warning: null };
  if (isTypeAllowedForCompany(requested, companyServiceType)) {
    return { type: requested, warning: null };
  }
  return {
    type: fallback,
    warning: `${AR_REQUIRES_AR_COMPANY_MESSAGE}. Se crea como ${fallback}.`,
  };
}

/** Igual que resolveExpedientTypeWithWarning, pero solo el tipo. */
function resolveExpedientType(params) {
  return resolveExpedientTypeWithWarning(params).type;
}

module.exports = {
  AR_REQUIRES_AR_COMPANY_MESSAGE,
  isTypeAllowedForCompany,
  normalizeExpedientType,
  resolveExpedientType,
  resolveExpedientTypeWithWarning,
};
